import type { AdminClaims } from './admin-claims';

/**
 * Decode a JWT's payload into {@link AdminClaims}. Does NOT verify the
 * signature — signature validation is the backend's job; the UI only reads
 * already-trusted tokens and uses claims for routing + UI gating.
 *
 * Throws on malformed tokens. Returns `null` when the provided value is empty.
 */
export function decodeAdminJwt(token: string | null | undefined): AdminClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('decodeAdminJwt: expected a JWT with 3 parts');
  }
  const payloadB64 = parts[1]!;
  const padded = payloadB64 + '=='.slice(0, (4 - (payloadB64.length % 4)) % 4);
  const normalized = padded.replace(/-/g, '+').replace(/_/g, '/');

  let json: string;
  if (typeof atob === 'function') {
    json = atob(normalized);
  } else {
    // Node fallback (used by vitest in node env).
    json = Buffer.from(normalized, 'base64').toString('utf-8');
  }
  const parsed = JSON.parse(json) as Record<string, unknown>;

  // Narrow to AdminClaims — we trust the backend to stamp correct shapes.
  const claims: AdminClaims = {
    sub: String(parsed.sub ?? ''),
    exp: Number(parsed.exp ?? 0),
    ...(parsed.sa != null ? { sa: String(parsed.sa) as '1' | '0' } : {}),
    ...(parsed.tenant != null ? { tenant: String(parsed.tenant) } : {}),
    ...(parsed.orgId != null ? { orgId: String(parsed.orgId) } : {}),
    ...(parsed.ns != null ? { ns: String(parsed.ns) as '1' | '0' } : {}),
    // PROD-1781 — the explicit role grant. Without it on the shape,
    // `roleFromClaims` had nothing to check but scoping claims.
    ...(parsed.orgRoles != null ? { orgRoles: String(parsed.orgRoles) } : {}),
  };
  return claims;
}
